"use client";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { Component, type ErrorInfo, type ReactNode } from "react";

type QueryErrorBoundaryProps = {
    children: ReactNode;
    /** what to render instead of children; `retry` refetches the failed queries */
    fallback: (retry: () => void) => ReactNode;
    /** where it failed, for the error log, e.g. "program/shows" */
    context: string;
};

export default function QueryErrorBoundary(props: QueryErrorBoundaryProps) {
    return <QueryErrorResetBoundary>
        {({ reset }) => <ErrorBoundary {...props} onReset={reset} />}
    </QueryErrorResetBoundary>;
}

class ErrorBoundary extends Component<QueryErrorBoundaryProps & { onReset: () => void }, { error: Error | null }> {
    state = { error: null as Error | null };

    static getDerivedStateFromError(error: Error) {
        return { error };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        // info.componentStack says which component threw, which is the context needed to fix it
        window.dataLayer?.push({ event: "ERROR", eventCategory: this.props.context, eventLabel: error.message });
    }

    retry = () => {
        this.props.onReset();          // let TanStack fetch again
        this.setState({ error: null }); // render the children again
    };

    render() {
        return this.state.error ? this.props.fallback(this.retry) : this.props.children;
    }
}